import { CollectionViewer } from '@angular/cdk/collections';
import { AgrDataSource, FilterableDataSource } from '@next/core-lib/table-v2';
import { Observable, ReplaySubject, Subject, throwError, BehaviorSubject } from 'rxjs';
import { catchError, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

import { CowWithMeasurement } from '../models/cow.model';
import { CowService } from '../services/cow.service';

export interface CowDataFilters {
  measurementDate?: Date;
  allowEmptyMeasurements?: boolean;
}

export class CowDataSource
  extends AgrDataSource<CowWithMeasurement>
  implements FilterableDataSource<CowDataFilters, CowWithMeasurement>
{
  private readonly _filtersSubject = new ReplaySubject<CowDataFilters>(1);
  private readonly _destroySubject = new Subject<void>();
  private _sortColumn: 'earNumber' | 'ageInMonths' | null = null;
  private _sortDirection: 'asc' | 'desc' | null = null;
  private readonly _rawData$ = new BehaviorSubject<CowWithMeasurement[]>([]);
  public readonly results$: Observable<readonly CowWithMeasurement[]>;
  public readonly totalCount$: Observable<number>;

  constructor(private readonly _cowService: CowService) {
    super();

    this._filtersSubject.next({});

    const fetchedData$ = this._filtersSubject.pipe(
      tap(() => this.loadingSubject.next(true)),
      switchMap((filters) => this._cowService.getCowsForMeasurement(filters)),
      tap((data) => {
        this._rawData$.next(data ?? []);
        this.loadingSubject.next(false);
      }),
      catchError((error) => {
        this.loadingSubject.next(false);
        return throwError(() => error);
      }),
      takeUntil(this._destroySubject),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    fetchedData$.subscribe();

    this.results$ = this._rawData$.pipe(
      map((data) => this.sortData(data)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.totalCount$ = this.results$.pipe(
      map((cows) => cows.length),
      catchError(() => {
        return throwError(() => new Error('Failed to get total count'));
      }),
    );
  }

  connect(_: CollectionViewer): Observable<readonly CowWithMeasurement[]> {
    return this.results$;
  }

  disconnect(_: CollectionViewer): void {
    if (!this._destroySubject.closed) {
      this._destroySubject.next();
      this._destroySubject.complete();
    }
  }

  applyFilters(filters: CowDataFilters): void {
    this._filtersSubject.next(filters);
  }

  getCows$(): Observable<readonly CowWithMeasurement[]> {
    return this.results$;
  }

  public setSorting(
    column: 'earNumber' | 'ageInMonths' | null,
    direction: 'asc' | 'desc' | null,
  ): void {
    this._sortColumn = column;
    this._sortDirection = direction;
    this._rawData$.next(this._rawData$.value);
  }

  private sortData(data: CowWithMeasurement[]): CowWithMeasurement[] {
    if (!this._sortColumn || !this._sortDirection) {
      return data;
    }

    const sorted = [...data];
    sorted.sort((a, b) => {
      let comparison = 0;

      if (this._sortColumn === 'earNumber') {
        const aVal = a.earNumber || '';
        const bVal = b.earNumber || '';
        comparison = aVal.localeCompare(bVal, undefined, { numeric: true });
      } else if (this._sortColumn === 'ageInMonths') {
        comparison = (a.ageInMonths || 0) - (b.ageInMonths || 0);
      }

      return this._sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }
}
